import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuItem from "@mui/material/MenuItem";
import RegisterSignature from "../task/RegisterSignature";
import SelectUser from "../generic/SelectUser";
import { ROLES } from "../../constants";
import { listPqrsdGroups } from "../../services/backend/PqrsdConfigService";
import { dependenciesOfUser, describeDependencies } from "./reviewerOptions";

// Builds the requerimiento de información the citizen will answer (CPACA
// art. 17). Items are typed on purpose rather than free prose: only a
// structured list can be validated on submit, marked satisfied item by item,
// and used as evidence if the desistimiento has to be decreed later.

const MAX_ITEMS = 20;

// Who may be named as a reviewer: the roles carrying pqrsd.approve_response,
// which approve on any dependency, plus the supervisors of the dependency this
// PQRSD is routed to (see canReviewThis). Anyone else — an operator, or the lead
// of another dependency — would be mailed and then refused by the server.
const REVIEWER_ROLES = Object.keys(ROLES).filter((role) =>
  (ROLES[role] || []).includes("pqrsd.approve_response")
);
const MIN_HEADER = 5;

const newItem = (kind) => ({
  key: `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  kind,
  header: "",
  label: "",
  help: "",
  required: true,
});

// Turns a stored requerimiento back into editable rows, so a second officer
// opens exactly what the first one wrote.
const toRows = (stored) =>
  (Array.isArray(stored) ? stored : [])
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((it, idx) => ({
      key: `${it.kind}_${it.id || idx}`,
      kind: it.kind === "file" ? "file" : "question",
      header: it.header || "",
      label: it.label || "",
      help: it.help || "",
      required: it.required !== false,
    }));

export default function InfoRequestBuilderDialog({
  open,
  onClose,
  onSend,
  onSaveDraft,
  onSubmitReview,
  busy,
  requiresApproval = false,
  citizenReplyDays = 30,
  request = null,
  // The dependency this PQRSD was routed to: the reviewer list opens on it,
  // because the people who can speak to this file are the ones working it.
  groupId = null,
}) {
  // Whole user objects, the shape SelectUser works in; only the ids are sent.
  const [reviewers, setReviewers] = useState([]);
  // Sending files the requerimiento and puts the sender's name in its
  // signature block, so it takes the same consent the flow tasks take. Saving a
  // draft or routing it to review signs nothing, and asks for nothing.
  const [signed, setSigned] = useState(false);
  // Routing it for approval hands the draft to someone else and starts a wait
  // the citizen never sees, so it gets a confirmation of its own.
  const [confirmReview, setConfirmReview] = useState(false);
  const [items, setItems] = useState([newItem("question")]);
  // The PQRSD dependencies, for naming each candidate's and filtering on them.
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  // Read only to warn when a dependency has no approver in it; SelectUser
  // loads and owns this list.
  const allUsers = useSelector((state) => state.user.users);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    (async () => {
      const list = await listPqrsdGroups();
      if (alive) setGroups(list);
    })();
    return () => { alive = false; };
  }, [open]);

  // Reload the form whenever the dialog opens — on a blank draft, or on the
  // requerimiento someone else left under review.
  useEffect(() => {
    if (!open) return;
    const rows = toRows(request?.items);
    setItems(rows.length ? rows : [newItem("question")]);
    setNote(request?.note || "");
    setReviewers([]);
    setSigned(false);
    setConfirmReview(false);
    setError(null);
    setGroupFilter(groupId || "");
  }, [open, request, groupId]);

  // What shows on the right of each name: where they work, and whether they run
  // it — naming the dependency's own supervisor is usually the whole point of
  // the pick, since their sign-off settles it instead of passing it along.
  const dependencyLabel = (candidate) => {
    const { names, supervisor, fallback } = describeDependencies(candidate, groups, groupFilter);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, justifyContent: "flex-end" }}>
        <span>{fallback || names.join(" · ")}</span>
        {supervisor && (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label="Supervisor"
            sx={{ height: 18, fontSize: 11 }}
          />
        )}
      </Box>
    );
  };

  const inGroupFilter = (candidate) =>
    !groupFilter || dependenciesOfUser(candidate, groups).some((g) => g.id === groupFilter);

  // The same rule the server applies on approve: an entity-wide approver, or a
  // supervisor of this PQRSD's own dependency.
  const canReviewThis = (candidate) =>
    candidate?.roles?.some((r) => REVIEWER_ROLES.includes(r)) ||
    (!!groupId &&
      dependenciesOfUser(candidate, groups).some((g) => g.id === groupId && g.supervisor));

  const reviewerFilter = (candidate) => canReviewThis(candidate) && inGroupFilter(candidate);

  // Nobody in the chosen dependency can approve: say so, rather than leave an
  // empty picker that reads as a broken one. Same list SelectUser draws from.
  const noCandidates =
    !!groupFilter &&
    Array.isArray(allUsers) &&
    !allUsers.some(reviewerFilter);

  const editing = !!request?.id;
  const underReview = request?.status === "pending_approval";

  const update = (key, patch) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const remove = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

  const move = (key, delta) =>
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.key === key);
      const next = idx + delta;
      if (idx < 0 || next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[next]] = [copy[next], copy[idx]];
      return copy;
    });

  const add = (kind) => {
    setError(null);
    setItems((prev) => (prev.length >= MAX_ITEMS ? prev : [...prev, newItem(kind)]));
  };

  const validate = () => {
    if (!items.length) return "Agregue al menos una pregunta o un documento.";
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === "question" && it.header.trim().length < MIN_HEADER) {
        return `La pregunta ${i + 1} necesita un enunciado de al menos ${MIN_HEADER} caracteres.`;
      }
      if (it.kind === "file" && !it.label.trim()) {
        return `El documento ${i + 1} necesita un nombre (por ejemplo "Cédula").`;
      }
    }
    return null;
  };

  const payload = () => ({
    requestId: request?.id || undefined,
    reviewerIds: reviewers.map((r) => r.userId).filter(Boolean),
    items: items.map((it, idx) => ({
      id: `it_${idx + 1}`,
      kind: it.kind,
      header: it.kind === "question" ? it.header.trim() : undefined,
      label: it.kind === "file" ? it.label.trim() : undefined,
      help: it.help.trim() || undefined,
      required: it.required,
    })),
    note: note.trim() || null,
  });

  // Every exit through an action validates first; only the action differs.
  const run = async (fn, fallback) => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    try {
      await fn(payload());
    } catch (e) {
      setError(e?.message || fallback);
    }
  };

  const submit = async () => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    // Only the forced-approval route leaves the signing to someone else.
    if (!requiresApproval && !signed) {
      setError("Registre su firma electrónica para enviar el requerimiento.");
      return;
    }
    setError(null);
    try {
      await onSend({
        requestId: request?.id || undefined,
        reviewerIds: reviewers.map((r) => r.userId).filter(Boolean),
        items: items.map((it, idx) => ({
          id: `it_${idx + 1}`,
          kind: it.kind,
          header: it.kind === "question" ? it.header.trim() : undefined,
          label: it.kind === "file" ? it.label.trim() : undefined,
          help: it.help.trim() || undefined,
          required: it.required,
        })),
        note: note.trim() || null,
      });
      setItems([newItem("question")]);
      setNote("");
    } catch (e) {
      setError(e?.message || "No fue posible enviar el requerimiento.");
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {editing ? "Revisar el requerimiento" : "Solicitar información al ciudadano"}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          El ciudadano recibirá un enlace seguro para responder en línea. Mientras tanto{" "}
          <strong>el término de respuesta queda suspendido</strong> y se reactivará al día siguiente
          de recibir la información. El ciudadano tendrá {citizenReplyDays} días calendario; si no
          responde, podrá decretarse el desistimiento tácito (artículo 17 de la Ley 1755 de 2015).
        </Alert>
        {requiresApproval && !underReview && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta entidad exige revisión: el requerimiento quedará pendiente de aprobación y solo se
            enviará cuando otro funcionario lo apruebe.
          </Alert>
        )}
        {underReview && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este requerimiento está en revisión{request?.issuedByName ? `, redactado por ${request.issuedByName}` : ""}.
            Puede agregar o quitar puntos; para aprobarlo y enviarlo se necesita un funcionario
            distinto de quien lo redactó.
          </Alert>
        )}
        {request?.rejectionComment && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Devuelto por {request.rejectedByName || "un revisor"}: {request.rejectionComment}
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} divider={<Divider flexItem />}>
          {items.map((it, idx) => (
            <Box key={it.key}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  icon={it.kind === "file" ? <UploadFileIcon /> : <HelpOutlineIcon />}
                  label={it.kind === "file" ? "Documento" : "Pregunta"}
                  color={it.kind === "file" ? "secondary" : "primary"}
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">#{idx + 1}</Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title={it.required ? "Obligatorio" : "Opcional"}>
                  <Button size="small" onClick={() => update(it.key, { required: !it.required })}>
                    {it.required ? "Obligatorio" : "Opcional"}
                  </Button>
                </Tooltip>
                <IconButton size="small" onClick={() => move(it.key, -1)} disabled={idx === 0}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => move(it.key, 1)} disabled={idx === items.length - 1}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => remove(it.key)} disabled={items.length === 1}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>

              {it.kind === "question" ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Pregunta"
                  placeholder="¿Cuál es la dirección exacta del predio?"
                  value={it.header}
                  onChange={(e) => update(it.key, { header: e.target.value })}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre del documento"
                  placeholder="Cédula"
                  value={it.label}
                  onChange={(e) => update(it.key, { label: e.target.value })}
                />
              )}
              <TextField
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                label="Indicación para el ciudadano (opcional)"
                placeholder={it.kind === "file" ? "Ambas caras en un solo PDF" : "Incluya barrio y nomenclatura"}
                value={it.help}
                onChange={(e) => update(it.key, { help: e.target.value })}
              />
            </Box>
          ))}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => add("question")} disabled={items.length >= MAX_ITEMS}>
            Agregar pregunta
          </Button>
          <Button size="small" startIcon={<AddIcon />} onClick={() => add("file")} disabled={items.length >= MAX_ITEMS}>
            Agregar documento
          </Button>
        </Stack>

        {(onSubmitReview || requiresApproval) && !underReview && (
          <Box sx={{ mt: 3 }}>
            {groups.length > 0 && (
              <TextField
                select
                size="small"
                label="Dependencia"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                sx={{ minWidth: 280, mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <GroupsIcon fontSize="small" color="disabled" sx={{ mr: 1 }} />
                  ),
                }}
              >
                <MenuItem value="">Todas las dependencias</MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </TextField>
            )}
            {noCandidates && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Nadie de esa dependencia puede aprobar un requerimiento. Elija «Todas las
                dependencias» o deje el campo vacío para avisar a los supervisores.
              </Alert>
            )}
            <SelectUser
              userType="users"
              multiple
              noAdd
              noedit
              // Shows each candidate's dependency on the right of their name,
              // and narrows the list to one dependency at a time.
              renderSecondary={dependencyLabel}
              // Only people who can actually approve this requerimiento. A
              // reviewer who cannot approve is a dead end: they would be mailed
              // and then have nothing to click.
              optionFilter={reviewerFilter}
              value={reviewers}
              onChange={(__, picked) => setReviewers(Array.isArray(picked) ? picked : [])}
              placeholder="Quién debe revisarlo (opcional)"
              helperText={
                reviewers.length
                  ? "Se les avisa por correo que hay un requerimiento por revisar."
                  : groupFilter
                  ? "Solo se listan los aprobadores de esa dependencia. Si no elige a nadie, se avisa a sus supervisores."
                  : "Si no elige a nadie, se avisa a los supervisores de la dependencia."
              }
            />
          </Box>
        )}

        <TextField
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 3 }}
          label="Mensaje adicional para el ciudadano (opcional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {!requiresApproval && (
          <Box sx={{ mt: 3 }}>
            <RegisterSignature
              actionName="firmado y enviado"
              edit={!busy}
              signed={signed}
              setSigned={setSigned}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Box sx={{ flex: 1 }} />
        {onSaveDraft && (
          <Button
            onClick={() => run(onSaveDraft, "No fue posible guardar el borrador.")}
            disabled={busy}
          >
            Guardar borrador
          </Button>
        )}
        {onSubmitReview && !underReview && (
          <Button
            variant="outlined"
            onClick={() => {
              const problem = validate();
              if (problem) {
                setError(problem);
                return;
              }
              setError(null);
              setConfirmReview(true);
            }}
            disabled={busy}
          >
            Enviar a revisión
          </Button>
        )}
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || (!requiresApproval && !signed)}
        >
          {requiresApproval && !underReview ? "Enviar a aprobación" : "Enviar requerimiento"}
        </Button>
      </DialogActions>

      <Dialog open={confirmReview} onClose={() => setConfirmReview(false)} maxWidth="xs" fullWidth>
        <DialogTitle>¿Enviar a revisión?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            El requerimiento queda en la <strong>bandeja «Casos a revisar»</strong> y se avisa a quien deba
            revisarlo. Al ciudadano no se le envía nada todavía, y{" "}
            <strong>el término de la entidad sigue corriendo</strong> hasta que se apruebe.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {reviewers.length
              ? `Se avisará a: ${reviewers.map((r) => `${r.name || ""} ${r.lastName || ""}`.trim()).join(", ")}.`
              : "No eligió revisores, así que se avisará a los supervisores de la dependencia."}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Puede retirarlo de la cola mientras nadie lo haya aprobado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReview(false)} disabled={busy}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={async () => {
              setConfirmReview(false);
              await run(onSubmitReview, "No fue posible enviar a revisión.");
            }}
          >
            Enviar a revisión
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
